import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGdpr } from './GdprContext';

/**
 * Swipe View — eligibility + open/close orchestration (SMASH-1853).
 *
 * Shared by the layouts that use the classic lightbox click path (Grid,
 * List, Masonry — see spec §2.1/§10 and the CarouselLayout/GalleryLayout
 * notes in the ticket report for what's deliberately NOT wired here).
 *
 * Gating (spec §10):
 *   - Pro only, and ineffective (not just invisible) in Free — mirrors the
 *     existing `(isPro && feedSettings?.videoPlayer) || 'lightbox'` pattern
 *     in PostImage.js so free behaviour is provably unchanged regardless of
 *     what a stored setting says.
 *   - GDPR §2.4: the viewer MUST NOT bind at all without consent; fall back
 *     to the caller's existing (GDPR-aware) lightbox instead.
 *
 * Enablement is the THIRD value of the existing per-feed `videoPlayer`
 * setting (post-review correction, PR #191), not a setting of its own. Two
 * earlier placements were tried and rejected:
 *
 *   1. A per-feed boolean — redundant with `videoPlayer`, which already
 *      answers "what happens when this feed's post is clicked".
 *   2. A GLOBAL plugin setting (`sbtt_feed_options.swipeView` /
 *      `sb_customizer.pluginSettings.swipeView`) — the plugin's global
 *      settings object holds only five site-wide operational keys; every
 *      rendering/behaviour decision here is per-feed, and the only channel
 *      from PHP to a feed's components is the `data-feed-settings` JSON
 *      (`feedSettings`) — never `window.sbtt_feed_options` /
 *      `window.sb_customizer`. Reading a global here returned `undefined`
 *      in the post components, which was not a wiring bug — it was the
 *      architecture rejecting the placement.
 *
 * `videoPlayer` is a `toggleset` ("Video Player Experience") whose entire
 * job is deciding what a click on a post does; `'swipe'` is a third answer
 * to that same question (alongside `'lightbox'` / `'inline'`), so a feed is
 * inline OR lightbox OR swipe — never a combination that can contradict
 * itself. `'swipe'` is the exact value string: a cross-repo contract with
 * the tiktok-feeds plugin PR that adds it as the third `videoPlayer` option
 * (awesomemotive/tiktok-feeds#116).
 *
 * Eligibility (spec §2.1, TikTok row): whole feed, minus posts with an empty
 * `video_url` — the relay mapper never inspects `aweme_type`, so photo-mode
 * posts arrive with no `video_url` rather than being filtered upstream
 * (local-notes/swipe-view-launch/phase0/tiktok-findings.md §2).
 *
 * Snapshot semantics (spec §2.3): the eligible track is captured ONCE, at
 * the moment the viewer opens, and never recomputed while it's open.
 */
export default function useSwipeViewer({ isPro, feedSettings }) {
    const { gdprActive, gdprConsented } = useGdpr();

    const [isSwipeOpen, setIsSwipeOpen] = useState(false);
    const [swipeTrack, setSwipeTrack] = useState([]);
    const [swipeStartIndex, setSwipeStartIndex] = useState(0);
    const openerRef = useRef(null);
    const wasOpenRef = useRef(false);

    const gdprBlocksSwipe = gdprActive && !gdprConsented;
    const swipeEnabled = !!isPro && feedSettings?.videoPlayer === 'swipe' && !gdprBlocksSwipe;

    const isSwipeEligible = useCallback((post) => !!post?.video_url, []);

    /*
     * §6.1 interaction counts (SMASH-1853). Rides the EXISTING per-feed
     * `postElements` checkboxlist rather than adding switches of its own
     * (maintainer decision 2026-08-24): 'likes' is already the toggle that
     * decides whether this feed shows engagement on its tiles, and 'views' the
     * view count — so a site that has turned tile counts off does not suddenly
     * get them back inside the viewer. Same "extend, don't multiply" rule §10.1
     * applies to enablement.
     *
     * 'likes' governs the whole engagement trio (likes + comments + shares):
     * TikTok's tile UI only ever exposed likes and views, so comments and
     * shares have no toggle of their own to inherit, and inventing two would
     * contradict the decision above.
     */
    const counts = useMemo(() => {
        const elements = Array.isArray(feedSettings?.postElements) ? feedSettings.postElements : [];
        return {
            engagement: elements.indexOf('likes') !== -1,
            views: elements.indexOf('views') !== -1,
        };
    }, [feedSettings]);

    const openSwipeView = useCallback(
        (posts, clickedPost, triggerEl) => {
            const track = posts.filter(isSwipeEligible); // snapshot — §2.3.
            const startIndex = track.findIndex((p) => p.id === clickedPost.id);
            if (startIndex === -1) return false;
            openerRef.current = triggerEl || document.activeElement;
            setSwipeTrack(track);
            setSwipeStartIndex(startIndex);
            setIsSwipeOpen(true);
            return true;
        },
        [isSwipeEligible]
    );

    const closeSwipeView = useCallback(() => {
        setIsSwipeOpen(false);
    }, []);

    // §7.1 focus restoration: the opener is passed in explicitly (never
    // inferred) and re-focused here at the point the state that actually
    // owns "is the viewer open" flips — not via Radix's onCloseAutoFocus,
    // which (per the existing Modal.jsx SMASH-1377 comment on this same
    // codebase) does not reliably have a trigger ref to restore to when a
    // dialog is conditionally mounted rather than opened via DialogTrigger.
    useEffect(() => {
        if (wasOpenRef.current && !isSwipeOpen) {
            const opener = openerRef.current;
            if (opener && opener.focus) opener.focus();
        }
        wasOpenRef.current = isSwipeOpen;
    }, [isSwipeOpen]);

    /**
     * Routes a post click: Swipe View when the post is eligible AND the
     * feature is enabled; otherwise `fallbackOpen` (the layout's existing
     * classic-lightbox handler) — unchanged, per spec §2.2's requirement
     * that a non-eligible post always keeps the existing lightbox.
     */
    const handlePostActivate = useCallback(
        (posts, postIndex, triggerEl, fallbackOpen) => {
            const post = posts[postIndex];
            if (swipeEnabled && isSwipeEligible(post) && openSwipeView(posts, post, triggerEl)) {
                return;
            }
            fallbackOpen(postIndex, triggerEl);
        },
        [swipeEnabled, isSwipeEligible, openSwipeView]
    );

    return {
        swipeEnabled,
        counts,
        isSwipeOpen,
        swipeTrack,
        swipeStartIndex,
        opener: openerRef.current,
        handlePostActivate,
        closeSwipeView,
    };
}
